import React, { useState, useEffect } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography, Paper, CircularProgress, Stack, Box, InputAdornment } from '@mui/material';
import { useTheme, keyframes } from '@mui/material/styles';
import { AttachMoney, WarningAmber, CheckCircle, Cancel, SwapHoriz } from "@mui/icons-material";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-5px); }
  to { opacity: 1; transform: translateY(0); }
`;

const BrandNegotiationDialog = ({
  open,
  handleClose,
  creator,
  currentOffer,
  onRespond,
  isSubmitting
}) => {
  const theme = useTheme();
  const [action, setAction] = useState(null);
  const [message, setMessage] = useState('');
  const [counterOffer, setCounterOffer] = useState('');
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!open) {
      setAction(null);
      setMessage('');
      setCounterOffer('');
      setValidationError('');
    }
  }, [open]);

  const validateOffer = (value) => {
    if (!value || isNaN(value) || parseFloat(value) <= 0) {
      return 'Please enter a valid offer amount';
    }
    return '';
  };

  const handleSubmit = () => {
    if (!action) {
      setValidationError('Please select an action');
      return;
    }

    if (action === 'counter') {
      const error = validateOffer(counterOffer);
      if (error) {
        setValidationError(error);
        return;
      }
    }

    const payload = {
      creator_id: creator.id,
      action,
      message,
      ...(action === 'counter' && { new_offer: parseFloat(counterOffer), new_status: 'Pending' })
    };

    onRespond(payload);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 4,
          boxShadow: theme.shadows[10],
          border: `1px solid ${theme.palette.divider}`
        }
      }}
    >
      <Paper elevation={0}>
        <DialogTitle
          sx={{
            background: `linear-gradient(45deg, ${theme.palette.secondary.dark}, ${theme.palette.secondary.main})`,
            color: theme.palette.common.white,
            py: 3,
            borderTopLeftRadius: 4,
            borderTopRightRadius: 4,
            borderBottom: `1px solid ${theme.palette.divider}`
          }}
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <SwapHoriz fontSize="medium" />
            <Typography variant="h6" fontWeight="600">
              Respond to {creator?.name || 'Creator'}'s Offer
            </Typography>
          </Stack>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 3 }}>
          <Stack spacing={3}>
            <Box sx={{
              backgroundColor: theme.palette.grey[100],
              borderRadius: 2,
              p: 2,
              border: `1px solid ${theme.palette.divider}`
            }}>
              <Typography variant="subtitle1" color="text.secondary">
                Current Counter Offer
              </Typography>
              <Typography variant="h4" fontWeight="700" color="secondary">
                ${currentOffer}
              </Typography>
            </Box>

            <Stack spacing={2}>
              <Button
                fullWidth
                variant={action === 'accept' ? "contained" : "outlined"}
                color="success"
                onClick={() => setAction('accept')}
                startIcon={<CheckCircle />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderWidth: action === 'accept' ? 0 : 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                Accept Counter Offer
              </Button>

              <Button
                fullWidth
                variant={action === 'decline' ? "contained" : "outlined"}
                color="error"
                onClick={() => setAction('decline')}
                startIcon={<Cancel />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderWidth: action === 'decline' ? 0 : 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                Decline Counter Offer
              </Button>

              <Button
                fullWidth
                variant={action === 'counter' ? "contained" : "outlined"}
                color="warning"
                onClick={() => setAction('counter')}
                startIcon={<AttachMoney />}
                sx={{
                  py: 1.5,
                  borderRadius: 2,
                  borderWidth: action === 'counter' ? 0 : 2,
                  '&:hover': { borderWidth: 2 }
                }}
              >
                Propose New Offer
              </Button>
            </Stack>

            {action === 'counter' && (
              <Box sx={{ animation: `${fadeIn} 0.3s ease-in` }}>
                <TextField
                  fullWidth
                  label="New Offer Amount"
                  variant="filled"
                  type="number"
                  value={counterOffer}
                  onChange={(e) => {
                    setCounterOffer(e.target.value.replace(/[^0-9.]/g, ''));
                    setValidationError('');
                  }}
                  inputProps={{ min: 0.01, step: 0.01 }}
                  error={!!validationError}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney color="action" />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '1.1rem', fontWeight: 500 }
                  }}
                  helperText={validationError && (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <WarningAmber fontSize="small" color="error" sx={{ mr: 1 }} />
                      <Typography variant="body2" color="error">
                        {validationError}
                      </Typography>
                    </Box>
                  )}
                />
                
                
              </Box>
            )}

            <TextField
              fullWidth
              variant="filled"
              multiline
              rows={3}
              label="Additional Message (Optional)"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              sx={{
                '& .MuiFilledInput-root': {
                  borderRadius: 2,
                  backgroundColor: theme.palette.grey[50]
                }
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Stack direction="row" spacing={2} width="100%">
            <Button 
              onClick={handleClose} 
              variant="outlined" 
              color="inherit"
              fullWidth
              sx={{
                borderRadius: 2,
                py: 1,
                borderWidth: 2,
                '&:hover': { borderWidth: 2 }
              }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="primary"
              onClick={handleSubmit}
              disabled={!action || (action === 'counter' && !counterOffer) || isSubmitting}
              fullWidth
              sx={{
                borderRadius: 2,
                py: 1,
                boxShadow: theme.shadows[2],
                '&:hover': { boxShadow: theme.shadows[4] }
              }}
            >
              {isSubmitting ? (
                <CircularProgress size={24} sx={{ color: theme.palette.common.white }} />
              ) : (
                'Submit Response'
              )}
            </Button>
          </Stack>
        </DialogActions>
      </Paper>
    </Dialog>
  );
};

export default BrandNegotiationDialog;